import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContractPack } from '@core/models/contract-pack/contract-pack.model';
import { BaseService } from '@core/services/base/base.service';
import { ErrorHandlerService, HttpError } from '@core/services/error-handler.service';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ContractPackFilter {
  year: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContractPackService extends BaseService {
  constructor(private httpService: HttpClient, private errorHandlerService: ErrorHandlerService) {
    super();
  }

  public getList(filter: ContractPackFilter): Observable<ContractPack[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récupérer la liste des packs. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_pack/' + filter.year;

    return new Observable(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            let contractPacks: ContractPack[] = [];
            if (response && response['statusCode'] === 200) {
              contractPacks = response['data'].map(contractPack => new ContractPack(contractPack));
            }
            return contractPacks;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public createPack(contractPack): Observable<ContractPack> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de créer le pack. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_pack';

    return new Observable(observer => {
      this.httpService
        .post(url, contractPack)
        .pipe(
          map(response => {
            if (response && response['statusCode'] === 200) {
              return new ContractPack(response['data']);
            }
            return null;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public updatePack(contractPack): Observable<ContractPack> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de modifier le pack. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_pack/' + contractPack.id;

    return new Observable(observer => {
      this.httpService
        .put(url, contractPack)
        .pipe(
          map(response => {
            if (response && response['statusCode'] === 200) {
              return new ContractPack(response['data']);
            }
            return null;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public deletePack(contractPackId: number): Observable<boolean> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de supprimer le pack. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_pack/' + contractPackId;

    return new Observable(observer => {
      this.httpService
        .delete(url)
        .pipe(
          map(response => {
            if (response && response['statusCode'] === 200) {
              return true;
            }
            return false;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }
}
